import { getAbsoluteUrl } from "$utils/urls.js";
import { csv } from "d3";
import { setContext, getContext } from "svelte";
import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
import {
  bigDollarFormat,
  dollarFormat,
  percentFormat,
  precisePercentFormat,
  roundFormat
} from "./formats.js";

export class ToolData {
  allData = $state();
  establishedData = $derived(this.isReady() ? this.processGroupData("Established") : []);
  troubledData = $derived(this.isReady() ? this.processGroupData("Troubled") : []);
  emergingData = $derived(this.isReady() ? this.processGroupData("Emerging") : []);
  thrivingData = $derived(this.isReady() ? this.processGroupData("Thriving") : []);
  metricsToShow = [
    [
      "Average VantageScore",
      "Median Credit Card Utilization",
      "Share with Mortgage",
      "Median Mortgage Balance"
    ],
    [
      "Share with 30+ Day Owner Delinquency",
      "Share with 90+ Day Business Delinquency",
      "Share of Firms in Business Record",
      "Median Business Credit Balance"
    ]
  ];
  visibleMetricIndex = $state(0);
  formatsForMetrics = [
    roundFormat,
    percentFormat,
    precisePercentFormat,
    bigDollarFormat,
    percentFormat,
    precisePercentFormat,
    percentFormat,
    dollarFormat
  ];
  groupData = $derived([
    {
      group: "Established",
      color: urbanColors.blue
    },
    {
      group: "Troubled",
      color: urbanColors.yellow
    },
    {
      group: "Emerging",
      color: urbanColors.magenta
    },
    {
      group: "Thriving",
      color: urbanColors.green
    }
  ]);

  metricData = $derived(
    this.metricsToShow.flat().map((metric, i) => ({
      metric: metric,
      min: Math.min(...this.getAllMetricValues(metric)),
      max: Math.max(...this.getAllMetricValues(metric)),
      changeMin: Math.min(...this.getAllMetricChangeValues(metric)),
      changeMax: Math.max(...this.getAllMetricChangeValues(metric)),
      format: this.formatsForMetrics[i]
    }))
  );

  async fetchData() {
    this.allData = await csv(getAbsoluteUrl("/all_long_data.csv"), (d) => {
      return {
        category: d.category,
        metric: d.metric,
        type: d.type == "preloan" ? 0 : d.type == "postloan" ? 1 : d.type,
        value: +d.value
      };
    });
  }

  isReady() {
    let isReady = this.allData && this.allData.length > 0;
    return isReady || false;
  }
  /** @param {string} group */
  processGroupData(group) {
    if (!this.isReady()) return [];
    return this.allData
      .filter((d) => d.category == "Median")
      .concat(this.allData.filter((d) => d.category == group));
  }

  /** @param {string} metric */
  getAllMetricValues(metric) {
    if (!this.isReady()) return [0, 10000];
    return this.allData.filter((d) => d.metric == metric && d.type != "change").map((d) => d.value);
  }

  /** @param {string} metric */
  getAllMetricChangeValues(metric) {
    if (!this.isReady()) return [0, 10000];
    return this.allData.filter((d) => d.metric == metric && d.type == "change").map((d) => d.value);
  }

  /** @param {string} group */
  getGroupData(group) {
    switch (group) {
      case "Established":
        return this.establishedData;
      case "Troubled":
        return this.troubledData;
      case "Emerging":
        return this.emergingData;
      case "Thriving":
        return this.thrivingData;
    }
  }

  /** @param {string} group */
  getGroupColor(group) {
    let thisGroup = this.groupData.find((d) => d.group == group);
    if (!thisGroup) return urbanColors.blue;
    return thisGroup["color"];
  }

  /** @param {string} metric */
  getMinMax(metric) {
    if (!this.isReady()) return [0, 1000];
    return [
      this.metricData.find((d) => d.metric == metric).min,
      this.metricData.find((d) => d.metric == metric).max
    ];
  }

  /** @param {string} metric */
  getChangeMinMax(metric) {
    if (!this.isReady()) return [0, 1000];
    return [
      Math.min(0, this.metricData.find((d) => d.metric == metric).changeMin),
      Math.max(this.metricData.find((d) => d.metric == metric).changeMax, 0)
    ];
  }

  /** @param {string} metric */
  getMetricFormat(metric) {
    let thisGroup = this.metricData.find((d) => d.metric == metric);
    if (!thisGroup) return roundFormat;
    return thisGroup["format"];
  }

  swapVisibleMetricSet() {
    this.visibleMetricIndex = (this.visibleMetricIndex + 1) % 2;
  }
}

let contextKey = "TOOL_DATA";

export function createData() {
  const data = new ToolData();
  setContext(contextKey, data);
  return data;
}

export function useData() {
  return getContext(contextKey);
}
